import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import SvgIcon from '@mui/material/SvgIcon';
import TextField from '@mui/material/TextField';
import { AlertCircle, Close as CloseIcon } from '@opengov/react-capital-assets';
import { CONFIRM_DELETE } from '@opengov/stories-shared/constants/stories';
import React, { useState } from 'react';
import { styles } from './DeleteConfirmationModal.styles';
import locale from './deleteConfirmationModal-locale.json';
import { circularProgressSizeProps } from '@opengov/capital-mui-theme';

type Props = {
  title: string | React.ReactNode;
  message: string | React.ReactNode;
  isDeleting: boolean;
  onClose: () => void;
  onDelete: () => void;
  dataTest?: string;
};

export function DeleteConfirmationModal({ title, message, isDeleting, onClose, onDelete, dataTest }: Props) {
  const [confirmText, setConfirmText] = useState('');
  const isConfirmValid = confirmText === CONFIRM_DELETE;

  return (
    <Dialog
      open={true}
      onClose={onClose}
      sx={styles.dialog}
      data-test={dataTest ?? 'delete-confirmation-modal'}
      disableEscapeKeyDown={isDeleting}
      maxWidth="sm"
    >
      <DialogTitle sx={styles.dialogTitle}>
        <Box sx={styles.iconWrapper}>
          <SvgIcon component={AlertCircle} inheritViewBox />
        </Box>
        {title}
      </DialogTitle>

      <IconButton
        className="MuiDialog__close-button"
        aria-label="close"
        onClick={onClose}
        disabled={isDeleting}
        data-test="close-confirmation-modal-button"
      >
        <SvgIcon component={CloseIcon} inheritViewBox />
      </IconButton>

      <DialogContent sx={styles.dialogContent}>
        <Box sx={styles.messageWrapper}>{message}</Box>

        <Box sx={styles.confirmationInputWrapper}>
          <TextField
            label={locale.typeDeleteToConfirm}
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            sx={styles.textField}
            inputProps={{ className: 'no-format', 'data-test': 'delete-confirmation-input' }}
            disabled={isDeleting}
            autoFocus
            data-test="delete-confirmation-text-field"
            autoComplete="off"
          />
        </Box>
      </DialogContent>

      <DialogActions>
        <Button
          onClick={onClose}
          variant="text"
          sx={styles.cancelButton}
          disabled={isDeleting}
          data-test="cancel-delete-button"
        >
          {locale.cancel}
        </Button>
        <Button
          onClick={() => {
            if (isConfirmValid) {
              onDelete();
            }
          }}
          variant="contained"
          disabled={!isConfirmValid || isDeleting}
          endIcon={
            isDeleting ? <CircularProgress {...circularProgressSizeProps.extraXSmall} sx={styles.loadingIcon} /> : null
          }
          data-test="confirm-delete-button"
          color="error"
        >
          {locale.delete}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
